import React from 'react';
import { X, Edit3, Image, MessageCircle, Receipt, ExternalLink } from 'lucide-react';

interface Slot {
  id: string;
  tripTitle: string;
  driveLink?: string;
  billLink?: string;
  notes?: string;
  info?: string;
  whatsAppLink?: string;
  [key: string]: any;
}

interface ViewSlotDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  slot: Slot;
  onEditField: (fieldName: string, fieldValue: string) => void;
  onClose: () => void;
}

const ViewSlotDialog: React.FC<ViewSlotDialogProps> = ({
  open,
  setOpen,
  slot,
  onEditField,
  onClose
}) => {
  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleEditField = (fieldName: string, fieldValue: string) => {
    onEditField(fieldName, fieldValue);
    setOpen(false);
  };

  const renderField = (
    title: string,
    value: string | undefined,
    fieldName: string,
    icon: React.ReactNode,
    isLink: boolean = false
  ) => {
    return (
      <div className="p-4 border-b border-gray-200 last:border-b-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            {icon}
            <h4 className="text-lg font-medium text-gray-900">{title}</h4>
          </div>
          <button
            onClick={() => handleEditField(fieldName, value || '')}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit3 className="h-4 w-4" />
          </button>
        </div>
        
        <div className="text-sm text-gray-600">
          {value ? (
            isLink ? (
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Tap to open</span>
              </a>
            ) : (
              <div className="whitespace-pre-wrap">{value}</div>
            )
          ) : (
            <span className="text-gray-400 italic">Not Available</span>
          )}
        </div>
      </div>
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="slot-dialog-title">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 id="slot-dialog-title" className="text-lg font-semibold text-gray-900 truncate">
            {slot?.tripTitle?.slice(0, 32) + (slot?.tripTitle?.length > 32 ? "..." : "")}
          </h3>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          {renderField(
            'Photos',
            slot.driveLink,
            'driveLink',
            <Image className="h-5 w-5 text-gray-500" />,
            true
          )}
          
          {renderField(
            'WhatsApp',
            slot.whatsAppLink,
            'whatsAppLink',
            <MessageCircle className="h-5 w-5 text-gray-500" />,
            true
          )}
          
          {renderField(
            'Receipts',
            slot.billLink,
            'billLink',
            <Receipt className="h-5 w-5 text-gray-500" />,
            true
          )}
          
          {renderField(
            'Notes',
            slot.notes,
            'notes',
            <Edit3 className="h-5 w-5 text-gray-500" />
          )}
          
          {renderField(
            'Information',
            slot.info,
            'info',
            <Edit3 className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewSlotDialog;
